/* Built In Imports */
import { useRouter } from 'next/router';
import React, { createRef, useEffect, useRef, useState } from 'react';

/* External Imports */
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalOverlay,
    Select,
    Text,
    useDisclosure
} from '@chakra-ui/react';
import { Field, Form, Formik } from 'formik';
import parser from 'html-react-parser';
import { nanoid } from 'nanoid';
import ReCAPTCHA from 'react-google-recaptcha';
import * as Yup from 'yup';

/* Internal Imports */
/* Components */
import InputUI from '@components/Elements/InputUI';
import config from '@config';
import { addSubscription } from 'services/msrService';
import { CountriesList } from '../../../../../constants';

/* Services */

/**
 * Renders the Subscribe Box component
 *
 * @param {object} newsletters
 * @param {string} type
 * @param {string} inputBackground
 * @param {string} submitText
 * @param {boolean} hideSpamText Want to show Spam text or not
 * @param newsletters.sectionContent
 * @param newsletters.startDate
 * @returns {ReactElement} Subscribe Box component.
 */
const SubscribeBox = ({ sectionContent, startDate }) => {
  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const finalRef = useRef(null);
  const [messageTemp, setMessageTemp] = useState('');
  const [loading, setLoading] = useState(false);
  const countries = CountriesList;
  const recaptchaRef = createRef();
  React.memo(countries);

  const ValidationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(/^([a-zA-Z ])+$/u, {
        message: 'Name is invalid',
      })
      .max(100, 'Name is invalid')
      .required('Name is required'),
    email: Yup.string()
      .required('Email is required')
      .matches(/^[a-zA-Z]+[a-zA-Z0-9._]+@[a-zA-Z]+(\.[a-zA-Z]{2,15})+$/, {
        message: 'Email is invalid',
      }),
    country: Yup.string().required('Country is required'),
    recaptcha: Yup.string().required('Cannot go past captcha'),
  });

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    let formValues = values;
    setSubmitting(true);
    let formResponse = await addSubscription(formValues);
    setSubmitting(false);

    resetForm();
    
    if (typeof formResponse === 'string') {
      setMessageTemp(
        `<div >Sorry something went wrong. We are not able to process your request right now.</div>`
      );
    } else if (
      formResponse[0]?.status_code === 101 ||
      formResponse[0]?.status_code === 102 ||
      formResponse[0]?.status_code === 103 ||
      formResponse[0]?.status_code === 104 ||
      formResponse[0]?.status_code === 105 ||
      formResponse[0]?.status_code === 106
    ) {
      setMessageTemp(formResponse[0]?.message_template);
    } else {
      setMessageTemp(
        `<div >Sorry something went wrong. We are not able to process your request right now.</div>`
      );
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'msr_vp_pushed',
      virtual_path: '/shiva_followers_vp_success',
    });
    onOpen();
    setLoading(false);
    const popupUrl =
      router.asPath.indexOf('?popup=1') > -1
        ? router.asPath
        : router.asPath.indexOf('?') > -1
        ? `${router.asPath}&popup=1`
        : `${router.asPath}?popup=1`;
    window.history.pushState(window.history.state, '', popupUrl);
    window['loc'] = popupUrl;
  };

  useEffect(() => {
    if (!isOpen && window?.loc?.indexOf('popup=1') > -1) {
      const parentUrl =
        location.origin +
        location.pathname +
        location.search.replace(/[\?&]popup=1/, '').replace(/^&/, '?');
      window.history.pushState(window.history.state, '', parentUrl);
    }
    recaptchaRef.current.reset();
  }, [isOpen]);

  useEffect(() => {
    window.onpopstate = e => {
      // consoleLog('onpopstate', router, window, e);
      if (window?.loc?.indexOf('popup=1') > -1) {
        // consoleLog('onpopstate return', router.asPath);
        onClose();
        window['loc'] = router.asPath;
      }
    };
  }, [router.asPath]);

  return (
    <>
      <Flex flexDir="column" w={{ base: '95%', md: '370px' }} mx="auto">
        <Formik
          initialValues={{
            name: '',
            email: '',
            country: '',
            'opt-in': '',
            newsletters: { ShivaFollowers: 'ShivaFollowers' },
            source: `${config.BASE_PATH}${router.asPath}`,
            date: startDate,
            recaptcha: '',
          }}
          validationSchema={ValidationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {props => (
            <Form
              onSubmit={props.handleSubmit}
              style={{
                display: 'grid',
                rowGap: '25px',
                boxSizing: 'border-box',
              }}
            >
              <Field name="name">
                {({ field, form }) => (
                  <FormControl
                    isRequired
                    isInvalid={form.errors.name && form.touched.name}
                    display="flex"
                    flexDir="column"
                    fontFamily="FedraSansStd-book"
                  >
                    <InputUI
                      // wd={{ md: '215px', sm: '460px', base: '300px' }}
                      // m={{ md: '0 0 0 15px', sm: '0', base: '0' }}
                      fontFamily="'FedraSansStd-book', sans-serif"
                      color="#605D5D"
                      field={field}
                      id="lastName"
                      height="56px"
                      placeholder={
                        sectionContent?.content?.json?.name || 'Full Name'
                      }
                      onChange={props.handleChange}
                      padding="20px"
                      borderRadius="6px"
                      _placeholder={{
                        color: '#605D5D',
                        fontSize: '16px',
                      }}
                    />
                    <FormErrorMessage justifyContent="left">
                      {form.errors.name}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="email">
                {({ field, form }) => (
                  <FormControl
                    isRequired
                    isInvalid={form.errors.email && form.touched.email}
                  >
                    <InputUI
                      fontFamily="'FedraSansStd-book', sans-serif"
                      color="#605D5D"
                      border="0.3px solid #000000"
                      field={field}
                      id="email"
                      height="56px"
                      placeholder="Email"
                      borderRadius="6px"
                      onChange={props.handleChange}
                      padding="20px"
                      _placeholder={{
                        color: '#605D5D',
                        fontSize: '16px',
                      }}
                    />
                    <FormErrorMessage justifyContent="left">
                      {form.errors.email}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="country">
                {({ field, form }) => (
                  <FormControl
                    isRequired
                    isInvalid={form.errors.country && form.touched.country}
                    width="100%"
                    m={{ base: '0 0 10px 0' }}
                  >
                    <Select
                      key="country"
                      {...field}
                      id="country"
                      width="100%"
                      height="56px"
                      backgroundColor="#fff"
                      isRequired={true}
                      placeholder={
                        sectionContent?.content?.json?.country || 'Country'
                      }
                      fontFamily="FedraSansStd-book, sans-serif"
                      color="#605D5D"
                      borderRadius="6px"
                      border="1px solid #aea493"
                      borderBottom="1px solid #a1a1a1"
                      borderTop="none"
                      borderRight="none"
                      borderLeft="none"
                      padding="0"
                      outline="0"
                      _placeholder={{
                        color: '#605D5D',
                        fontSize: '16px',
                      }}
                    >
                      {CountriesList.map(country => {
                        return (
                          <option key={nanoid()} value={country.value}>
                            {country.name}
                          </option>
                        );
                      })}
                    </Select>
                    <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
              <Field name="recaptcha">
                {({ field, form }) => (
                  <FormControl
                    isInvalid={form.errors.recaptcha && form.touched.recaptcha}
                    //   width={{ base: 'auto', xl: 'calc((100% - 40px)/3)' }}
                    m="0 auto"
                    textAlign="center"
                    d="flex"
                    placeContent="center"
                  >
                    {config.RECAPTCHA_KEY && (
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={config.RECAPTCHA_KEY}
                        onChange={success => {
                          success === null
                            ? recaptchaRef.current.reset()
                            : props.setFieldValue('recaptcha', success);
                        }}
                        onErrored={error => {
                          props.setFieldValue('recaptcha', '');
                          recaptchaRef.current.reset();
                        }}
                      />
                    )}
                    <FormErrorMessage justifyContent="left">
                      {form.errors.recaptcha}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Box display="grid" placeContent="center">
                <Button
                  type="submit"
                  className="orange-button"
                  mb={'39px'}
                  display="block"
                  fontWeight={'500'}
                  fontFamily={'FedraSansStd-medium'}
                  fontSize={'16px'}
                  bgColor="yellow.100"
                  h={'auto'}
                  minH="50px"
                  border="none"
                  textAlign="center"
                  padding={'18px 20px'}
                  borderRadius="5px"
                  color="black"
                  textDecoration="none"
                  width={'auto'}
                  _hover={{
                    bg: 'fulvous',
                  }}
                  disabled={
                    !props.isValid || !props.dirty || loading ? true : false
                  }
                >
                  <Flex justifyContent="center" alignItems="center">
                    <Text pr={'5px'}>
                      {loading ? (
                        'Submitting..'
                      ) : (
                        <span>
                          Subscribe{' '}
                          <Text
                            display="inline"
                            position={'relative'}
                            fontSize="20px"
                            fontWeight="bold"
                            top="1px"
                            left="5px"
                          >
                            {' '}
                            &gt;{' '}
                          </Text>
                        </span>
                      )}
                    </Text>
                  </Flex>
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Flex>

      {messageTemp && (
        <Box
          maxW={messageTemp ? { base: '90%', md: '476px' } : 'auto'}
          m="20px auto"
        >
          <Modal
            finalFocusRef={finalRef}
            isOpen={isOpen}
            onClose={onClose}
            isCentered
            motionPreset="slideInBottom"
            size={!messageTemp ? 'xl' : '4xl'}
            blockScrollOnMount={true}
            closeOnOverlayClick={false}
            // scrollBehavior="inside"
          >
            <ModalOverlay />
            <ModalContent p="30px">
              <ModalCloseButton color="#9F9F9F" mt="1" />
              <ModalBody>{parser(messageTemp || '')}</ModalBody>
            </ModalContent>
          </Modal>
        </Box>
      )}
    </>
  );
};

export default SubscribeBox;
